import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import api from '../../config/axiosConfig';
import 'react-toastify/dist/ReactToastify.css';
import { API_URLS, POSITION_OPTIONS } from '../../config/Constants';
import { showErrorMsg, showSuccessMsg } from '../../config/common';
import { sessionManager } from '../../config/sessionManager';
import { Modal, ModalTitle, ModalContent, ModalActions } from '../basic';

interface AddPlayerModalProps {
  onClose: () => void;
  onPlayerAdded?: () => void;
  editPlayer?: any | null;
}

// Zod validation schema
const playerSchema = z.object({
  no: z.number().min(0, 'Player number is required and must be 0 or greater'),
  firstName: z.string().min(1, 'First name is required').trim(),
  lastName: z.string().min(1, 'Last name is required').trim(),
  position: z.array(z.string()).min(1, 'At least one position is required'),
  graduation: z.string().min(1, 'Graduation is required').trim(),
  active: z.boolean(),
  clientId: z.string().min(1, 'Client is required'),
});

type PlayerFormData = z.infer<typeof playerSchema>;

const AddPlayerModal: React.FC<AddPlayerModalProps> = ({ onClose, onPlayerAdded, editPlayer }) => {
  const [isLoading, setIsLoading] = useState(false);
  const clientIdFromSession = sessionManager.getClientId();

  // Initialize react-hook-form with zod validation
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    control,
    reset,
  } = useForm<PlayerFormData>({
    resolver: zodResolver(playerSchema),
    defaultValues: {
      no: 0,
      firstName: '',
      lastName: '',
      position: [],
      graduation: '',
      active: true,
      clientId: '',
    },
  });

  // Fetch clients on component mount
  useEffect(() => {
    if (clientIdFromSession) {
      setValue('clientId', clientIdFromSession);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientIdFromSession]);

  // Pre-fill form if editing
  useEffect(() => {
    if (editPlayer) {
      reset({
        no: editPlayer.no || 0,
        firstName: editPlayer.firstName || '',
        lastName: editPlayer.lastName || '',
        position: editPlayer.position
          ? Array.isArray(editPlayer.position)
            ? editPlayer.position
            : [editPlayer.position]
          : [],
        graduation: editPlayer.graduation || '',
        active: !!editPlayer.active,
        clientId: editPlayer.clientId || '',
      });
    }
  }, [editPlayer, reset]);

  const handleClose = () => {
    if (!isLoading && !isSubmitting) {
      onClose();
    }
  };

  const onSubmit = async (data: PlayerFormData) => {
    const token = sessionManager.getToken();
    if (!token) {
      showErrorMsg('Authentication token not found. Please log in again.');
      return;
    }

    setIsLoading(true);
    try {
      let response;
      if (editPlayer && editPlayer._id) {
        // Update player
        const submitData = {
          ...data,
          _id: editPlayer._id,
        };
        response = await api.put(API_URLS.UPDATE_PLAYER, submitData);
        if (response.data.success) {
          showSuccessMsg('Player updated successfully!');
          onPlayerAdded?.();
          onClose();
        } else {
          showErrorMsg(response.data.message || 'Failed to update player');
        }
      } else {
        // Add player
        response = await api.post(API_URLS.ADD_PLAYER, data);
        if (response.data.success) {
          showSuccessMsg('Player added successfully!');
          onPlayerAdded?.();
          onClose();
        } else {
          showErrorMsg(response.data.message || 'Failed to add player');
        }
      }
    } catch (error: any) {
      console.error('Error adding/updating player:', error);
      if (error.response?.status === 401) {
        showErrorMsg('Session expired. Please log in again.');
      } else if (error.response?.data?.message) {
        showErrorMsg(error.response.data.message);
      } else if (error.message) {
        showErrorMsg(`Error: ${error.message}`);
      } else {
        showErrorMsg('Failed to add/update player. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={handleClose} maxWidth="md" maxHeight="80vh" isLoading={isLoading || isSubmitting}>
      <ModalTitle>{editPlayer ? 'Edit Player' : 'Add Player'}</ModalTitle>

      <ModalContent>
        <form id="player-form" onSubmit={handleSubmit(onSubmit)}>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-gray-700 font-medium mb-1">Player Number *</label>
              <input
                type="number"
                {...register('no', { valueAsNumber: true })}
                className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
                  errors.no ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={isLoading || isSubmitting}
              />
              {errors.no && <p className="text-red-500 text-sm mt-1">{errors.no.message}</p>}
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-1">First Name *</label>
              <input
                type="text"
                {...register('firstName')}
                className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
                  errors.firstName ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={isLoading || isSubmitting}
              />
              {errors.firstName && <p className="text-red-500 text-sm mt-1">{errors.firstName.message}</p>}
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-1">Last Name *</label>
              <input
                type="text"
                {...register('lastName')}
                className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
                  errors.lastName ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={isLoading || isSubmitting}
              />
              {errors.lastName && <p className="text-red-500 text-sm mt-1">{errors.lastName.message}</p>}
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-1">Graduation *</label>
              <input
                type="text"
                {...register('graduation')}
                className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
                  errors.graduation ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={isLoading || isSubmitting}
              />
              {errors.graduation && <p className="text-red-500 text-sm mt-1">{errors.graduation.message}</p>}
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-1">
                Positions<span className="text-red-500">*</span>
              </label>
              <div className="border rounded p-3">
                <Controller
                  name="position"
                  control={control}
                  render={({ field }) => (
                    <>
                      {POSITION_OPTIONS.map((pos) => (
                        <label key={pos} className="flex items-center mb-1">
                          <input
                            type="checkbox"
                            value={pos}
                            checked={field.value.includes(pos)}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              if (isChecked) {
                                field.onChange([...field.value, pos]);
                              } else {
                                field.onChange(field.value.filter((p: string) => p !== pos));
                              }
                            }}
                            className="mr-2"
                            disabled={isLoading || isSubmitting}
                          />
                          <span>{pos}</span>
                        </label>
                      ))}
                    </>
                  )}
                />
              </div>
              {errors.position && <p className="text-red-500 text-sm mt-1">{errors.position.message}</p>}
            </div>

            <div>
              <label className="flex items-center">
                <input type="checkbox" {...register('active')} className="mr-2" disabled={isLoading || isSubmitting} />
                <span className="text-gray-700 font-medium">Active</span>
              </label>
            </div>
          </div>
        </form>
      </ModalContent>

      <ModalActions>
        <button
          type="button"
          onClick={handleClose}
          className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 transition-colors disabled:opacity-50"
          disabled={isLoading || isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          form="player-form"
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          disabled={isLoading || isSubmitting}
        >
          {isLoading || isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {editPlayer ? 'Saving...' : 'Adding...'}
            </>
          ) : editPlayer ? (
            'Save Changes'
          ) : (
            'Add Player'
          )}
        </button>
      </ModalActions>
    </Modal>
  );
};

export default AddPlayerModal;
